import { Component, OnInit, ChangeDetectionStrategy, ChangeDetectorRef, OnDestroy, input, output, inject, OnChanges } from '@angular/core';

import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

// PrimeNG imports
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';

// Models and services

import { ToastMessageService } from '@fitness-force/api';
import { ProductCategoryService } from '../../services';
import { CreateProductCategoryRequest, ProductCategoryResponse, UpdateProductCategoryRequest } from '../../models';

@Component({
  selector: 'lib-category-form',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    TranslateModule,
    DialogModule,
    ButtonModule,
    InputTextModule
],
  template: `
    <p-dialog
      [visible]="visible()"
      [modal]="true"
      [closable]="true"
      [draggable]="false"
      [resizable]="false"
      [style]="{ width: '600px' }"
      (onHide)="onCancel.emit()">
    
      <ng-template pTemplate="header">
        <h3>{{ isEditMode ? ('RETAIL_BILLING.CATEGORY.EDIT' | translate) : ('RETAIL_BILLING.CATEGORY.CREATE' | translate) }}</h3>
      </ng-template>
    
      <!-- Level Type Header -->
      <div class="bg-primary-50 border-primary-100 border-1 border-round p-3 mb-4 flex align-items-center gap-2">
        <i class="pi pi-info-circle text-primary"></i>
        <span class="text-primary font-medium">
          {{ companyLevel() ? ('RETAIL_BILLING.PRODUCTS.COMPANY_LEVEL' | translate) : ('RETAIL_BILLING.PRODUCTS.CURRENT_TENANT' | translate) }}
        </span>
      </div>
    
      <form [formGroup]="categoryForm" class="grid">
        <!-- Category Name (Primary Language) -->
        <div class="col-12 md:col-6">
          <label for="name_primary">{{ 'RETAIL_BILLING.CATEGORY.NAME' | translate }}
            @if (isSecondaryLanguage()) {
              <span>({{ primaryLanguage() }})</span>
            }
            <sup class="text-red-500">*</sup>
          </label>
          <input
            pInputText
            id="name_primary"
            formControlName="name_primary_language"
            class="w-full mt-2"
            aria-label="category-name-primary-input" />
            @if (categoryForm.get('name_primary_language')?.invalid && categoryForm.get('name_primary_language')?.touched) {
              <small class="p-error">
                {{ 'RETAIL_BILLING.CATEGORY.NAME' | translate }} is required
              </small>
            }
          </div>
    
          <!-- Category Name (Secondary Language) -->
          @if (isSecondaryLanguage()) {
            <div class="col-12 md:col-6">
              <label for="name_secondary">{{ 'RETAIL_BILLING.CATEGORY.NAME' | translate }}
                <span>({{ secondaryLanguage() }})</span>
                <sup class="text-red-500">*</sup>
              </label>
              <input
                pInputText
                id="name_secondary"
                formControlName="name_secondary_language"
                class="w-full mt-2"
                dir="rtl"
                aria-label="category-name-secondary-input" />
                @if (categoryForm.get('name_secondary_language')?.invalid && categoryForm.get('name_secondary_language')?.touched) {
                  <small class="p-error">
                    {{ 'RETAIL_BILLING.CATEGORY.NAME' | translate }} is required
                  </small>
                }
              </div>
            }
    
            <!-- Description (Primary Language) -->
            <div class="col-12 md:col-6">
              <label for="desc_primary">{{ 'RETAIL_BILLING.CATEGORY.DESCRIPTION' | translate }}
                @if (isSecondaryLanguage()) {
                  <span>({{ primaryLanguage() }})</span>
                }
              </label>
              <textarea
                pInputTextarea
                id="desc_primary"
                formControlName="description_primary_language"
                rows="3"
                class="w-full mt-2"
                aria-label="category-description-primary-input">
              </textarea>
            </div>
    
            <!-- Description (Secondary Language) -->
            @if (isSecondaryLanguage()) {
              <div class="col-12 md:col-6">
                <label for="desc_secondary">{{ 'RETAIL_BILLING.CATEGORY.DESCRIPTION' | translate }}
                  <span>({{ secondaryLanguage() }})</span>
                </label>
                <textarea
                  pInputTextarea
                  id="desc_secondary"
                  formControlName="description_secondary_language"
                  rows="3"
                  class="w-full mt-2"
                  dir="rtl"
                  aria-label="category-description-secondary-input">
                </textarea>
              </div>
            }
    
          </form>
    
          <ng-template pTemplate="footer">
            <div class="flex justify-content-end gap-2">
              <p-button [label]="'RETAIL_BILLING.COMMON.CANCEL' | translate"
                icon="pi pi-times"
                [text]="true"
                (onClick)="onCancel.emit()"
                aria-label="cancel-button" />
              <p-button [label]="'RETAIL_BILLING.COMMON.SAVE' | translate"
                icon="pi pi-check"
                [loading]="saving"
                [disabled]="categoryForm.invalid"
                (onClick)="saveCategory()"
                aria-label="save-category-button" />
            </div>
          </ng-template>
        </p-dialog>
    `,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CategoryFormComponent implements OnInit, OnDestroy, OnChanges {
  private  fb = inject(FormBuilder);
  private readonly categoryService = inject(ProductCategoryService);
  private readonly toastService = inject(ToastMessageService);
  private readonly translate = inject(TranslateService);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);

  readonly visible = input(false);
  readonly category = input<ProductCategoryResponse | null>(null);
  readonly tenantId = input<number>();
  readonly companyLevel = input<boolean>(true);
  readonly primaryLanguage = input.required<string>();
  readonly secondaryLanguage = input.required<string>();
  readonly isSecondaryLanguage = input(false);

  readonly visibleChange = output<boolean>();
  readonly onSave = output<ProductCategoryResponse>();
  readonly onCancel = output<void>();

  categoryForm: FormGroup;
  saving = false;
  private destroy$ = new Subject<void>();

  get isEditMode(): boolean {
    return !!this.category();
  }

  constructor() {
    this.categoryForm = this.fb.group({
      name_primary_language: ['', Validators.required],
      name_secondary_language: [''],
      description_primary_language: ['', Validators.required],
      description_secondary_language: [''],
      status: [true]
    });
  }

  ngOnInit() {
    this.updateFormValidation();
  }

  ngOnChanges() {
    const category = this.category();
    const visible = this.visible();
    if (visible && category) {
      this.populateForm();
    } else if (visible && !category) {
      this.resetForm();
    }
   // this.updateFormValidation();
  }

  updateFormValidation() {
    if (this.categoryForm) {
      const nameSecondaryControl = this.categoryForm.get('name_secondary_language');
      if (this.isSecondaryLanguage()) {
        nameSecondaryControl?.setValidators([Validators.required]);
      } else {
        nameSecondaryControl?.clearValidators();
      }
      nameSecondaryControl?.updateValueAndValidity();
    }
  }

  populateForm() {
    const category = this.category();
    if (category) {
      this.categoryForm.patchValue({
        name_primary_language: category.name_primary_language || '',
        name_secondary_language: category.name_secondary_language || '',
        description_primary_language: category.description_primary_language || '',
        description_secondary_language: category.description_secondary_language || '',
        status: category.is_active
      });
    }
  }

  resetForm() {
    this.categoryForm.reset({
      status: true
    });
  }

  

  saveCategory() {
    if (this.categoryForm.valid) {
      this.saving = true;
      const formData = this.categoryForm.value;

      const request: CreateProductCategoryRequest | UpdateProductCategoryRequest = {
        name_primary_language: formData.name_primary_language || '',
        name_secondary_language: formData.name_secondary_language || '',
        description_primary_language: formData.description_primary_language || '',
        description_secondary_language: formData.description_secondary_language || '',
        is_active: formData.status
      };

      const saveOperation = this.isEditMode
        ? this.categoryService.updateCompanyCategory(this.category()!.id, request as UpdateProductCategoryRequest)
        : this.categoryService.createCompanyCategory(request as CreateProductCategoryRequest);

      saveOperation
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (response) => {
            this.saving = false;
            // Create a mock category response for the event
            const savedCategory: ProductCategoryResponse = {
              ...this.category(),
              id: this.category()?.id || (response as any).id || 0,
              ...request,
              created_at: this.category()?.created_at || new Date().toISOString(),
              updated_at: new Date().toISOString(),
              created_by: 1,
              updated_by: 1,
              company_id: 1
            } as ProductCategoryResponse;
            this.changeDetectorRef.detectChanges();
            this.onSave.emit(savedCategory);
            this.visibleChange.emit(false);
          },
          error: (error) => {
            this.saving = false;
            this.toastService.showError(error);
            this.changeDetectorRef.detectChanges();
          }
        });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.visibleChange.emit(false);
  }
}